import clsx from "clsx";
import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { SubscriptionAutoRenewal } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionAutoRenewal/SubscriptionAutoRenewal";
import { SubscriptionActionButton } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionActionButton/SubscriptionActionButton";
import { SubscriptionOptions } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionOptions/SubscriptionOptions";
import { SubscriptionPrice } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { SubscriptionStatus } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionStatus/SubscriptionStatus";
import { useSubscriptionActions } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/useSubscriptionActions";
import { useIsMobile } from "@/hooks/useIsMobile";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail } from "@/services/model";
import { isInactive, isLockRequested } from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

/**
 * The "Subscription" tab of the device detail view: which subscription runs on the SIM,
 * whether it renews, and what it costs.
 */
export const SubscriptionDisplay: FC = () => {
  const { data: simDetail } = useDisplayedSim();

  if (!simDetail) {
    return null;
  }

  // Mirrors SubscriptionHint's own branching below: it renders nothing unless
  // the SIM is inactive, or pending without a lock already requested. Needed
  // up front, as a plain boolean rather than the component's actual render,
  // so TwoColumnLayout can skip the 2-column grid when there is nothing to
  // pair with the cards.
  const isSimInactive = isInactive(simDetail);
  const hasLockRequested = isLockRequested(simDetail);
  const hasHint =
    isSimInactive || (!!simDetail.servicePending && !hasLockRequested);

  return (
    <TwoColumnLayout
      rows={[
        {
          left: (
            <div className="flex w-full flex-col gap-gutter">
              <SubscriptionSections simDetail={simDetail} />
            </div>
          ),
          right: hasHint && <SubscriptionHint simDetail={simDetail} />,
        },
      ]}
    />
  );
};

const SubscriptionSections = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription",
  });
  const { t: tDefault } = useTranslation();
  const { optionsDisabledHint, statusDisabledHint } =
    useSubscriptionActions(simDetail);
  const isMobile = useIsMobile();
  // On mobile, pausing/reactivating opens its own routed screen (Figma: a
  // dedicated page with its own navbar/tabbar). On desktop it replaces this
  // card in place, on the same page - no navigation, closing it returns here.
  const [isRenewalPanelOpen, setRenewalPanelOpen] = useState(false);
  // Crossing the breakpoint while the panel is open (window resize, not just
  // the initial render) would otherwise strand it open with no way back: the
  // mobile action button only ever navigates, it never calls onClose.
  useEffect(() => {
    if (isMobile) {
      setRenewalPanelOpen(false);
    }
  }, [isMobile]);
  // Single source of truth for "is the panel actually showing" - the effect
  // above only catches up after a render, so anything that must agree with
  // the panel (here: the greyed-out "Change") reads this, not the raw state,
  // or a resize could flash it disabled a beat after the panel is gone.
  const showRenewalPanel = !isMobile && isRenewalPanelOpen;

  const isSimInactive = isInactive(simDetail);
  const hasLockRequested = isLockRequested(simDetail);
  const isPending = !!simDetail.servicePending;
  // Flat Home options and billing period are fixed for the whole contract
  // term, so there is genuinely nothing to change here — those controls are
  // dropped entirely. Every other "not now" case keeps the button and greys
  // it out instead (see useSubscriptionActions).
  const canChangeOptions = !isFlatHomeTariff(simDetail.serviceID);

  // The "Good to know" hint (a sibling of this component, in the same grid)
  // pairs with whichever of these is the last one rendered - never with
  // SubscriptionPrice, since the hint's own conditions and Price's both
  // require `!isSimInactive` but never coincide on the same state (see
  // SubscriptionHint). Every card is pinned to column 1 of its own explicit
  // row instead of spanning both columns, so it stays capped to a single
  // column's width - column 2 is simply left blank in rows with no hint,
  // rather than the card stretching into it.
  const showStatusOrRenewal = !isSimInactive;
  const showPrice = !isSimInactive && (!isPending || hasLockRequested);

  return (
    <>
      <div className="tablet:col-start-1 tablet:row-start-1">
        <SubscriptionOptions
          simDetail={simDetail}
          action={
            canChangeOptions && (
              <SubscriptionActionButton
                dataTestid="subscription-change"
                label={
                  isSimInactive
                    ? t("chooseSubscription")
                    : tDefault("Common.label.change")
                }
                target={`${portalRoutes.manageDevice.subscription.change}?simId=${simDetail.simId}`}
                disabledHint={
                  // The subscription can't be changed while a pause/reactivate
                  // decision is still open for review.
                  showRenewalPanel
                    ? t("options.changeDisabledPauseReview")
                    : optionsDisabledHint
                }
              />
            )
          }
        />
      </div>

      {showStatusOrRenewal && (
        <div className="tablet:col-start-1 tablet:row-start-2">
          {showRenewalPanel ? (
            <SubscriptionAutoRenewal
              onClose={() => setRenewalPanelOpen(false)}
            />
          ) : (
            <SubscriptionStatus
              simDetail={simDetail}
              showBillingPeriod={canChangeOptions}
              billingPeriodDisabledHint={
                optionsDisabledHint ?? statusDisabledHint
              }
              action={
                <SubscriptionActionButton
                  dataTestid="subscription-pause"
                  label={
                    simDetail.autoRenew
                      ? t("status.pause")
                      : t("status.cancelPause")
                  }
                  disabledHint={statusDisabledHint}
                  {...(isMobile
                    ? {
                        target: `${portalRoutes.manageDevice.subscription.automaticRenewal}?simId=${simDetail.simId}`,
                      }
                    : { onClick: () => setRenewalPanelOpen(true) })}
                />
              }
            />
          )}
        </div>
      )}

      {/* The price is unknown while the order is still being prepared, but it
          stays visible once a payment failed, so the customer can see what the
          blocked subscription costs. Always the last row: it never coincides
          with the hint, so it never needs to pair with anything in column 2. */}
      {showPrice && (
        <div className="tablet:col-start-1 tablet:row-start-3">
          <SubscriptionPrice simDetail={simDetail} />
        </div>
      )}
    </>
  );
};

/** The "Good to know" panel shown beside (desktop) or below (mobile) the cards. */
const SubscriptionHint = ({ simDetail }: { simDetail: SimCardDetail }) => {
  const { t } = useTranslation();

  const isSimInactive = isInactive(simDetail);
  const hasLockRequested = isLockRequested(simDetail);

  // An active SIM carries the failed payment in its status card. An inactive
  // one has no status card, so without this branch the customer is never told
  // why the subscription stopped.
  // Pairs with whichever card sits in that row: SubscriptionOptions alone
  // when the SIM is inactive (row 1, no status card), or the status card
  // otherwise (row 2) - see the matching row-start classes in
  // SubscriptionSections.
  const rowClassName = isSimInactive
    ? "tablet:row-start-1"
    : "tablet:row-start-2";

  if (isSimInactive && hasLockRequested) {
    return (
      <Notification
        dataTestid="subscription-hint"
        className={clsx("tablet:col-start-2", rowClassName)}
        tone="error"
        title={t("portal:cockpit.managementTile.tariff.goodToKnow.warning")}
        description={
          <Trans
            t={t}
            i18nKey="portal:cockpit.managementTile.tariff.goodToKnow.missingPaymentMeanHint"
            components={[
              <Link
                key="0"
                size="sm"
                to={portalRoutes.documents.payments}
                replace
              />,
            ]}
          />
        }
      />
    );
  }

  if (isSimInactive) {
    return (
      <Notification
        dataTestid="subscription-hint"
        className={clsx("tablet:col-start-2", rowClassName)}
        title={t("Content.goodToKnow")}
        description={t(
          "portal:cockpit.managementTile.tariff.goodToKnow.inactiveHint",
        )}
      />
    );
  }

  if (simDetail.servicePending && !hasLockRequested) {
    return (
      <Notification
        dataTestid="subscription-hint"
        className={clsx("tablet:col-start-2", rowClassName)}
        title={t("Content.goodToKnow")}
        description={t(
          "portal:cockpit.managementTile.tariff.serviceIsPendingHint",
        )}
      />
    );
  }

  return null;
};
